import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import ProductList from '../../components/admin/ProductList';
import ProductForm from '../../components/admin/ProductForm';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/products');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setModalOpen(true);
  };

  const handleEdit = async (product) => {
    try {
      const response = await api.get(`/admin/products/${product.id}`);
      setSelectedProduct(response.data.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching product details:', error);
      alert('Failed to fetch product details');
    }
  };

  const handleSubmit = async (formData) => {
    // Validate file sizes before upload
    const images = formData.getAll('images');
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    for (let image of images) {
      if (image.size > maxSize) {
        alert(`Image "${image.name}" is too large. Maximum size is 2MB. Please compress it first.`);
        return;
      }
    }

    setSubmitting(true);
    
    try {
      let response;
      
      if (selectedProduct) {
        console.log('Updating product:', selectedProduct.id);
        response = await api.put(`/admin/products/${selectedProduct.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout
        });
        alert('✅ Product updated successfully!');
      } else {
        console.log('Creating new product...');
        response = await api.post('/admin/products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout
        });
        alert('✅ Product created successfully!');
      }
      
      console.log('Success:', response.data);
      
      // Close modal first
      setModalOpen(false);
      
      // Wait 2 seconds then refresh (allows server to update)
      setTimeout(() => {
        fetchProducts();
      }, 2000);
      
    } catch (error) {
      console.error('Error saving product:', error);
      
      let errorMessage = 'Failed to save product';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please try again with smaller images (under 2MB).';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('❌ ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
  if (!window.confirm('Are you sure you want to delete this product?')) {
    return;
  }

  try {
    console.log('Deleting product:', id);
    
    // Optimistic UI update
    const productToDelete = products.find(p => p.id === id);
    setProducts(prev => prev.filter(p => p.id !== id));
    
    const response = await api.delete(`/admin/products/${id}`, {
      timeout: 120000 // 2 minutes
    });
    
    if (response.data.success) {
      alert('✅ Product deleted successfully!');
      console.log('Delete successful');
    }
    
  } catch (error) {
    console.error('Delete error:', error);
    
    // If timeout or connection error, wait and check if it actually deleted
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout') || error.message.includes('Network Error')) {
      
      alert('⏳ Request timed out. Checking if deletion succeeded... Please wait.');
      
      // Wait 5 seconds then check if product still exists
      setTimeout(async () => {
        try {
          await api.get(`/admin/products/${id}`);
          // Product still exists - restore it
          alert('⚠️ Deletion failed. Product has been restored.');
          fetchProducts();
        } catch (checkError) {
          if (checkError.response?.status === 404) {
            // Product doesn't exist = deletion succeeded!
            alert('✅ Product was successfully deleted (server was slow to respond).');
          } else {
            // Unknown error
            alert('⚠️ Cannot verify deletion. Please refresh the page.');
            fetchProducts();
          }
        }
      }, 5000);
      
    } else {
      // Real error - restore product
      alert('❌ Failed to delete product: ' + (error.response?.data?.error || error.message));
      fetchProducts();
    }
  }
};

  if (loading) return <Loader fullscreen />;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">პროდუქტები</h1>
        <Button variant="primary" onClick={handleCreate}>
          ➕ პროდუქტის დამატება  
        </Button>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h2 className="table-title">ყველა პროდუქტი ({products.length})</h2>
        </div>
        <ProductList
          products={products}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedProduct ? 'Edit Product' : 'Create Product'}
      >
        <ProductForm
          product={selectedProduct}
          onSubmit={handleSubmit}
          onCancel={() => setModalOpen(false)}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default Products;